import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import MainLayout from '../../components/MainLayout';
import FactoryTargetModal from '../../components/factory/FactoryTargetModal';
import DailyReportModal from '../../components/DailyReportModal';
import WeeklyReportModal from '../../components/WeeklyReportModal';
import MonthlyReportModal from '../../components/MonthlyReportModal';
import FactoryFlag, { countryOfFactory } from '../../components/factory/FactoryFlag';
import { formatHeaderTime } from '../../components/factory/FactoryPageHeader';
import { fetchCompanyDashboard, fetchCompanyFactoryOverview, updateFactoryTargets } from '../../services/api';
import { hasPermission } from '../../services/authService';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useTranslation } from '../../context/LanguageContext';
import { useAlarmNotification } from '../../context/AlarmNotificationContext';
import './Enterprise.css';

/* ===== 전사 모니터링 > 통합 대시보드 — PNG 시안 =====
 * 헤더: 전사 › 통합 대시보드 + 보고서(일일/주간/월간) · 동기화 시각 · 새로 고침
 * 1행 KPI 8칸: 종합 OEE · 가용성 · 성능 · 양품률 · 불량률 · 설비 가동률 · 일일 생산량 · 에너지 (상태 배지 위험/경고/정상)
 * 2행: 종합 생산량·가동률 추이(90일) / OEE 구성 추이(90일)
 * 3행: 공장별 현황 / 전사 알람 / 생산 목표 대비 달성(오늘)
 *
 * 데이터 출처
 * - /dashboard/company  : kpiCards(가동·생산·에너지·양품/불량 수·목표·어제 값), oeeMetrics, productionTrend, oeeTrend, factoryStatuses
 * - /dashboard/company-factory : 공장별 위치·설비 수·Critical/Warning 알람 수·마지막 샷 시각(수집 상태)
 * - 알람 패널: AlarmNotificationContext(최근 알람 100건 폴링 · 심각도별 건수)
 *
 * 배지 기준(위험/경고/정상)은 저장된 설정이 없어 아래 THRESHOLDS 상수로 판정한다(화면 기준).
 * 시안의 "어제 대비" 알람 증감·금형 온도·공장별 목표는 데이터가 없어 넣지 않았다. 공장별 막대는 전사 목표 대비 그 공장 생산량(기여율)이다.
 * 예상 달성 시간은 오늘 0시부터의 평균 생산 속도로 남은 목표를 나눈 값이라 "추정" 표기. 스타일은 en- 접두어. */

const OEE_TARGET = 85;
const THRESHOLDS = {
  oee: { good: 85, warn: 60 },
  availability: { good: 85, warn: 60 },
  performance: { good: 85, warn: 70 },
  quality: { good: 99, warn: 97 },
  defect: { good: 1, warn: 3, lowerIsBetter: true },
  utilization: { good: 80, warn: 50 },
  production: { good: 90, warn: 70 },          /* 목표 대비 % */
  energy: { good: 10, warn: 25, lowerIsBetter: true } /* 전일 대비 증가 % */
};

const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
const num = (v) => (isNum(v) ? Number(v) : null);

const levelOf = (key, value) => {
  const th = THRESHOLDS[key];
  if (!th || value == null) return null;
  if (th.lowerIsBetter) return value <= th.good ? 'good' : value <= th.warn ? 'warn' : 'bad';
  return value >= th.good ? 'good' : value >= th.warn ? 'warn' : 'bad';
};

const fmt = (v, digits = 1) => (v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits }));
const fmtInt = (v) => (v == null ? '—' : Math.round(Number(v)).toLocaleString());

/* 상태 배지 */
function LevelBadge({ level, t }) {
  if (!level) return null;
  return <span className={`en-badge en-badge-${level}`}>{t(`pages.monitoring.enterprise.v2.level.${level}`)}</span>;
}

/* KPI 카드 */
function Kpi({ label, value, unit, sub, level, t }) {
  return (
    <div className={`en-kpi en-kpi-${level || 'none'}`}>
      <div className="en-kpi-head">
        <span className="en-kpi-label">{label}</span>
        <LevelBadge level={level} t={t} />
      </div>
      <div className="en-kpi-value">{value}{unit && value !== '—' && <small>{unit}</small>}</div>
      <div className="en-kpi-sub">{sub}</div>
    </div>
  );
}

/* 도넛 (달성률) */
function Ring({ percent, label }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const p = percent == null ? 0 : Math.max(0, Math.min(100, percent));
  return (
    <div className="en-ring">
      <svg viewBox="0 0 128 128" width="128" height="128">
        <circle cx="64" cy="64" r={r} className="en-ring-track" />
        <circle cx="64" cy="64" r={r} className="en-ring-fill" strokeDasharray={`${(p / 100) * c} ${c}`} transform="rotate(-90 64 64)" />
      </svg>
      <div className="en-ring-text">
        <b>{percent == null ? '—' : Math.round(percent)}<small>{percent == null ? '' : '%'}</small></b>
        <span>{label}</span>
      </div>
    </div>
  );
}

const CHART_TICK = { fill: '#98A8BF', fontSize: 11 };
const CHART_AXIS = { stroke: 'rgba(255,255,255,0.12)' };
const TOOLTIP = {
  contentStyle: { backgroundColor: '#1A1E24', border: '1px solid rgba(197,215,224,0.2)', borderRadius: 4, fontSize: 12 },
  labelStyle: { color: '#98A8BF' },
  itemStyle: { padding: 0 }
};
const OEE_SERIES = [
  { key: 'oee', color: '#4F8EF7', area: true },
  { key: 'availability', color: '#47CD89' },
  { key: 'performance', color: '#A78BFA' },
  { key: 'quality', color: '#FDB022', area: true }
];

function Enterprise() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = useCallback((k, p) => t(`pages.monitoring.enterprise.v2.${k}`, p), [t]);
  const { recentAlarms, counts: alarmCounts, openDetail, openList } = useAlarmNotification();

  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [modal, setModal] = useState(null);   /* 'target' | 'daily' | 'weekly' | 'monthly' */
  const [oeeVisible, setOeeVisible] = useState({ oee: true, availability: false, performance: false, quality: true });

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    const [dash, ov] = await Promise.allSettled([fetchCompanyDashboard(), fetchCompanyFactoryOverview()]);
    if (dash.status === 'fulfilled') { setData(dash.value); setError(null); }
    else { console.error('Dashboard data loading failed:', dash.reason); if (!silent) setError(tr('loadFailed')); }
    if (ov.status === 'fulfilled') setOverview(ov.value);
    else console.error('Company factory overview load failed:', ov.reason);
    setLastUpdated(new Date());
    if (silent) setTimeout(() => setRefreshing(false), 600); else setLoading(false);
  }, [tr]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true));

  /* ---- KPI ---- */
  const kpiValues = useMemo(() => {
    const kpi = data?.kpiCards || {};
    const oee = data?.oeeMetrics || {};
    const total = num(kpi.totalEquipment);
    const running = num(kpi.runningEquipment);
    const good = num(kpi.goodCount);
    const defect = num(kpi.defectCount);
    const judged = good != null && defect != null ? good + defect : null;
    const defectRate = judged ? (defect / judged) * 100 : (num(oee.quality) != null && Number(oee.quality) > 0 ? 100 - Number(oee.quality) : null);
    const production = num(kpi.dailyProduction);
    const target = num(kpi.targetProduction);   /* 공장 목표 합(targetSource=FACTORY) 또는 전사 단일 목표(COMPANY) */
    const energyKwh = num(kpi.energyConsumption) != null ? Number(kpi.energyConsumption) / 1000 : null;          /* 기존 화면과 같이 Wh → kWh */
    const yEnergyKwh = num(kpi.yesterdayEnergyConsumption) != null ? Number(kpi.yesterdayEnergyConsumption) / 1000 : null;
    return {
      oee: num(oee.oee),
      availability: num(oee.availability),
      performance: num(oee.performance),
      quality: num(oee.quality),
      defectRate,
      defectEstimated: !judged,
      defect,
      utilization: total ? (running / total) * 100 : null,
      running,
      total,
      production,
      target,
      targetRatio: target ? (production / target) * 100 : null,
      energyKwh,
      energyChange: yEnergyKwh ? ((energyKwh - yEnergyKwh) / yEnergyKwh) * 100 : null
    };
  }, [data?.kpiCards, data?.oeeMetrics]);

  /* ---- 추이 ---- */
  const dateLabel = (s) => {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  };
  const productionTrend = useMemo(() => {
    const p = data?.productionTrend;
    if (!p?.dates) return [];
    return p.dates.map((d, i) => ({ date: dateLabel(d), production: num(p.productionCounts?.[i]), rate: num(p.operatingRates?.[i]) }));
  }, [data?.productionTrend]);
  const oeeTrend = useMemo(() => {
    const o = data?.oeeTrend;
    if (!o?.dates) return [];
    return o.dates.map((d, i) => ({ date: dateLabel(d), oee: num(o.oee?.[i]), availability: num(o.availability?.[i]), performance: num(o.performance?.[i]), quality: num(o.quality?.[i]) }));
  }, [data?.oeeTrend]);

  /* ---- 공장별 ---- */
  const factories = useMemo(() => {
    const byId = new Map((overview?.factories || []).map((f) => [f.factoryId, f]));
    return (data?.factoryStatuses || []).map((fs) => {
      const ov = byId.get(fs.factoryId) || {};
      const eq = ov.equipment || {};
      const total = num(eq.total) ?? num(fs.totalEquipment);
      const stopped = num(eq.stopped) ?? num(fs.stoppedEquipment);
      /* 수집 상태 — 헤더의 데이터 수집 현황과 같은 기준: 정지 0대 정상 / 전부 정지 끊김 / 그 외 일부 */
      const collection = !total ? 'none' : stopped === 0 ? 'ok' : stopped === total ? 'lost' : 'partial';
      return {
        id: fs.factoryId,
        code: fs.factoryCode,
        name: fs.factoryName,
        country: countryOfFactory(fs.factoryName, ov.location),
        rate: num(fs.operatingRate) ?? num(ov.operationRate),
        production: num(fs.production) ?? num(ov.production),
        total,
        running: num(eq.running) ?? num(fs.runningEquipment),
        stopped,
        critical: num(ov.alarms?.error),
        warning: num(ov.alarms?.warning),
        activeAlarms: num(ov.alarms?.total) ?? num(fs.alarmCount),
        target: num(fs.targetProduction) > 0 ? num(fs.targetProduction) : null,   /* factory_production_target */
        collection
      };
    });
  }, [data?.factoryStatuses, overview]);
  const activeAlarmTotal = factories.reduce((s, f) => s + (f.activeAlarms || 0), 0);

  /* ---- 목표 ---- */
  const remaining = kpiValues.target != null && kpiValues.production != null ? Math.max(0, kpiValues.target - kpiValues.production) : null;
  const eta = useMemo(() => {
    if (!remaining || !kpiValues.production) return null;
    const now = new Date();
    const elapsedH = (now - new Date(now.getFullYear(), now.getMonth(), now.getDate())) / 3600000;
    const perHour = elapsedH > 0 ? kpiValues.production / elapsedH : 0;
    if (perHour <= 0) return null;
    const mins = Math.round((remaining / perHour) * 60);
    return mins;
  }, [remaining, kpiValues.production]);
  const etaLabel = remaining === 0 ? tr('target.done')
    : eta == null ? '—'
    : eta >= 60 ? tr('target.hm', { h: Math.floor(eta / 60), m: eta % 60 }) : tr('target.m', { m: eta });

  const sevKey = (s) => (s === 'CRITICAL' ? 'critical' : s === 'WARNING' ? 'warning' : 'info');
  const alarmList = recentAlarms.slice(0, 30);

  const CountryEn = { KR: 'Korea', VN: 'Vietnam' };

  return (
    <MainLayout>
      <div className="en-page">
        {/* ---- 헤더 ---- */}
        <div className="en-header">
          <div className="en-crumb">
            <span className="material-symbols-outlined en-crumb-icon">apartment</span>
            <span className="en-crumb-root">{tr('breadcrumbRoot')}</span>
            <span className="material-symbols-outlined en-crumb-sep">chevron_right</span>
            <span className="en-crumb-page">{tr('pageLabel')}</span>
          </div>
          <div className={`en-header-right ${refreshing ? 'refreshing' : ''}`}>
            <div className="en-reports">
              <button type="button" onClick={() => setModal('daily')}>{t('pages.monitoring.enterprise.reports.daily')}</button>
              <button type="button" onClick={() => setModal('weekly')}>{t('pages.monitoring.enterprise.reports.weekly')}</button>
              <button type="button" onClick={() => setModal('monthly')}>{t('pages.monitoring.enterprise.reports.monthly')}</button>
            </div>
            <span className="en-time">{formatHeaderTime(data?.lastSyncTime || lastUpdated)}</span>
            <button type="button" className="en-refresh" onClick={() => load(true)} title={tr('refresh')} aria-label={tr('refresh')}>
              <span className="material-symbols-outlined">refresh</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="en-error">
            {error}
            <button type="button" onClick={() => load(false)}>{tr('retry')}</button>
          </div>
        )}

        {/* ---- KPI 8칸 ---- */}
        <div className="en-kpis">
          <Kpi t={t} label={tr('kpi.oee')} value={fmt(kpiValues.oee)} unit="%" level={levelOf('oee', kpiValues.oee)}
            sub={kpiValues.oee == null ? '—' : tr('kpi.oeeSub', { target: OEE_TARGET, diff: `${kpiValues.oee - OEE_TARGET >= 0 ? '+' : ''}${(kpiValues.oee - OEE_TARGET).toFixed(1)}` })} />
          <Kpi t={t} label={tr('kpi.availability')} value={fmt(kpiValues.availability)} unit="%" level={levelOf('availability', kpiValues.availability)}
            sub={tr('kpi.baseline', { v: `${THRESHOLDS.availability.good}%` })} />
          <Kpi t={t} label={tr('kpi.performance')} value={fmt(kpiValues.performance)} unit="%" level={levelOf('performance', kpiValues.performance)}
            sub={levelOf('performance', kpiValues.performance) === 'good' ? tr('kpi.inRange') : tr('kpi.baseline', { v: `${THRESHOLDS.performance.good}%` })} />
          <Kpi t={t} label={tr('kpi.quality')} value={fmt(kpiValues.quality)} unit="%" level={levelOf('quality', kpiValues.quality)}
            sub={tr('kpi.baseline', { v: `${THRESHOLDS.quality.good}%` })} />
          <Kpi t={t} label={tr('kpi.defectRate')} value={fmt(kpiValues.defectRate, 2)} unit="%" level={levelOf('defect', kpiValues.defectRate)}
            sub={kpiValues.defectEstimated ? tr('kpi.defectEstimated') : tr('kpi.defectSub', { n: fmtInt(kpiValues.defect), v: `${THRESHOLDS.defect.good}%` })} />
          <Kpi t={t} label={tr('kpi.utilization')} value={kpiValues.utilization == null ? '—' : Math.round(kpiValues.utilization)} unit="%" level={levelOf('utilization', kpiValues.utilization)}
            sub={kpiValues.total ? tr('kpi.utilizationSub', { r: kpiValues.running ?? 0, n: kpiValues.total }) : '—'} />
          <Kpi t={t} label={tr('kpi.production')} value={fmtInt(kpiValues.production)} unit={tr('unitEa')} level={levelOf('production', kpiValues.targetRatio)}
            sub={kpiValues.targetRatio == null ? tr('kpi.noTarget') : tr('kpi.productionSub', { p: Math.round(kpiValues.targetRatio) })} />
          <Kpi t={t} label={tr('kpi.energy')} value={fmtInt(kpiValues.energyKwh)} unit="kWh" level={levelOf('energy', kpiValues.energyChange)}
            sub={kpiValues.energyChange == null ? tr('kpi.today') : tr('kpi.energySub', { p: `${kpiValues.energyChange >= 0 ? '+' : ''}${kpiValues.energyChange.toFixed(1)}` })} />
        </div>

        {/* ---- 추이 2칸 ---- */}
        <div className="en-charts">
          <section className="en-panel">
            <div className="en-panel-head">
              <h3 className="en-panel-title">{tr('chart.productionTitle')} <small>{tr('chart.days', { n: 90 })}</small></h3>
              <div className="en-legend">
                <span><i style={{ background: '#4F8EF7' }} />{tr('chart.production')}</span>
                <span><i style={{ background: '#A78BFA' }} />{tr('chart.rate')}</span>
              </div>
            </div>
            <div className="en-chart">
              {productionTrend.length === 0 ? <div className="en-empty">{loading ? tr('loading') : tr('noData')}</div> : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={productionTrend} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="enProd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4F8EF7" stopOpacity={0.45} />
                        <stop offset="100%" stopColor="#4F8EF7" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="date" tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} minTickGap={24} />
                    <YAxis yAxisId="l" tick={CHART_TICK} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => Number(v).toLocaleString()} />
                    <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={CHART_TICK} axisLine={false} tickLine={false} width={36} tickFormatter={(v) => `${v}%`} />
                    <Tooltip {...TOOLTIP} formatter={(v, name) => (name === 'rate' ? [`${fmt(v)}%`, tr('chart.rate')] : [fmtInt(v), tr('chart.production')])} />
                    <Area yAxisId="l" type="monotone" dataKey="production" stroke="#4F8EF7" strokeWidth={1.5} fill="url(#enProd)" isAnimationActive={false} />
                    <Line yAxisId="r" type="monotone" dataKey="rate" stroke="#A78BFA" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>

          <section className="en-panel">
            <div className="en-panel-head">
              <h3 className="en-panel-title">{tr('chart.oeeTitle')} <small>{tr('chart.days', { n: 90 })}</small></h3>
              <div className="en-legend en-legend-toggle">
                {OEE_SERIES.map((s) => (
                  <button key={s.key} type="button" className={oeeVisible[s.key] ? 'on' : ''} onClick={() => setOeeVisible((v) => ({ ...v, [s.key]: !v[s.key] }))}>
                    <i style={{ background: s.color }} />{tr(`chart.${s.key}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="en-chart">
              {oeeTrend.length === 0 ? <div className="en-empty">{loading ? tr('loading') : tr('noData')}</div> : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={oeeTrend} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                    <defs>
                      {OEE_SERIES.filter((s) => s.area).map((s) => (
                        <linearGradient key={s.key} id={`enOee-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
                          <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="date" tick={CHART_TICK} axisLine={CHART_AXIS} tickLine={false} minTickGap={24} />
                    <YAxis domain={[0, 100]} tick={CHART_TICK} axisLine={false} tickLine={false} width={36} tickFormatter={(v) => `${v}%`} />
                    <Tooltip {...TOOLTIP} formatter={(v, name) => [`${fmt(v)}%`, tr(`chart.${name}`)]} />
                    {OEE_SERIES.filter((s) => oeeVisible[s.key]).map((s) => (s.area
                      ? <Area key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.5} fill={`url(#enOee-${s.key})`} isAnimationActive={false} />
                      : <Line key={s.key} type="monotone" dataKey={s.key} stroke={s.color} strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} />))}
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </section>
        </div>

        {/* ---- 하단 3칸 ---- */}
        <div className="en-bottom">
          {/* 공장별 현황 */}
          <section className="en-panel">
            <div className="en-panel-head">
              <h3 className="en-panel-title">{tr('factory.title')}</h3>
              <button type="button" className="en-link" onClick={() => navigate('/enterprise-factory-overview')}>{tr('factory.activeAlarms', { n: activeAlarmTotal })}</button>
            </div>
            <div className="en-scroll en-factories">
              {factories.length === 0 && <div className="en-empty">{loading ? tr('loading') : tr('noData')}</div>}
              {factories.map((f) => (
                <button type="button" key={f.id} className={`en-factory en-collect-${f.collection}`} onClick={() => navigate(`/factory?factoryId=${f.id}`)}>
                  <div className="en-factory-head">
                    <FactoryFlag country={f.country} cls="en-flag" size={20} />
                    <span className="en-factory-name">{f.name}</span>
                    {f.country && <span className="en-factory-en">{CountryEn[f.country]}</span>}
                    {f.code && <span className="en-chip"><i />{f.code}</span>}
                    <span className={`en-collect en-collect-badge-${f.collection}`}>{tr(`factory.collect.${f.collection}`)}</span>
                  </div>
                  <div className="en-factory-metrics">
                    <div><span>{tr('factory.rate')}</span><b>{fmt(f.rate)}<small>%</small></b></div>
                    <div><span>{tr('factory.production')}</span><b>{fmtInt(f.production)}<small>{tr('unitEa')}</small></b></div>
                    <div><span>{tr('factory.equipment')}</span><b>{fmtInt(f.total)}<small>{tr('unitMachine')}</small></b></div>
                    <div><span>{tr('factory.running')}</span><b>{fmtInt(f.running)}<small>{tr('unitMachine')}</small></b></div>
                    <div><span>{tr('factory.stopped')}</span><b>{fmtInt(f.stopped)}<small>{tr('unitMachine')}</small></b></div>
                  </div>
                  <div className="en-factory-foot">
                    <span className="en-sev-critical">Critical · {f.critical ?? '—'}</span>
                    <span className="en-sev-warning">Warning · {f.warning ?? '—'}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* 전사 알람 */}
          <section className="en-panel">
            <div className="en-panel-head">
              <h3 className="en-panel-title">{tr('alarm.title')}</h3>
              <button type="button" className="en-link" onClick={openList}>{tr('alarm.all')}</button>
            </div>
            <div className="en-alarm-tiles">
              <div className="en-tile en-tile-critical"><span>{tr('alarm.critical')}</span><b>{fmtInt(alarmCounts.errorCount)}</b></div>
              <div className="en-tile en-tile-warning"><span>{tr('alarm.warning')}</span><b>{fmtInt(alarmCounts.warningCount)}</b></div>
              <div className="en-tile en-tile-info"><span>{tr('alarm.info')}</span><b>{fmtInt(alarmCounts.infoCount)}</b></div>
            </div>
            <div className="en-scroll en-alarms">
              {alarmList.length === 0 && <div className="en-empty">{tr('alarm.empty')}</div>}
              {alarmList.map((a) => (
                <button type="button" key={a.key} className={`en-alarm en-alarm-${sevKey(a.severity)}`} onClick={() => openDetail(a)}>
                  <div className="en-alarm-head">
                    <span className="en-alarm-title">{[a.factoryName, a.equipmentName].filter(Boolean).join(' · ')}</span>
                    <span className="en-alarm-time">{a.timeAgo || ''}</span>
                  </div>
                  <div className="en-alarm-msg">{a.message || a.code}</div>
                </button>
              ))}
            </div>
          </section>

          {/* 생산 목표 대비 달성 */}
          <section className="en-panel">
            <div className="en-panel-head">
              <h3 className="en-panel-title">{tr('target.title')}</h3>
              <button type="button" className="en-icon-btn" onClick={() => setModal('target')} title={t('pages.monitoring.enterprise.oee.targetSetting')} aria-label={t('pages.monitoring.enterprise.oee.targetSetting')}>
                <span className="material-symbols-outlined">settings</span>
              </button>
            </div>
            <div className="en-target">
              <Ring percent={kpiValues.targetRatio} label={tr('target.ratio')} />
              <dl className="en-target-rows">
                <div><dt>{tr('target.goal')}</dt><dd>{kpiValues.target == null ? tr('kpi.noTarget') : fmtInt(kpiValues.target)}</dd></div>
                <div><dt>{tr('target.actual')}</dt><dd>{fmtInt(kpiValues.production)}</dd></div>
                <div><dt>{tr('target.remaining')}</dt><dd>{fmtInt(remaining)}</dd></div>
              </dl>
            </div>
            <div className="en-eta">
              <span>{tr('target.eta')} <em className="en-tag">{tr('estimated')}</em></span>
              <b>{kpiValues.target == null ? '—' : etaLabel}</b>
            </div>
            <div className="en-scroll en-progress-list">
              {factories.map((f) => {
                /* 공장 목표 대비 그 공장 생산량 */
                const p = f.target && f.production != null ? (f.production / f.target) * 100 : null;
                return (
                  <div key={f.id} className="en-progress">
                    <div className="en-progress-name">
                      {f.name}
                      <span className="en-progress-goal">{f.target ? tr('target.factoryGoal', { a: fmtInt(f.production), g: fmtInt(f.target) }) : tr('target.factoryNoTarget')}</span>
                    </div>
                    <div className="en-progress-row">
                      <span className="en-progress-track"><i className={p != null && p >= 100 ? 'done' : ''} style={{ width: `${Math.min(100, p || 0)}%` }} /></span>
                      <span className="en-progress-pct">{p == null ? '—' : `${Math.round(p)}%`}</span>
                    </div>
                  </div>
                );
              })}
              {data?.kpiCards?.targetSource === 'COMPANY' && <p className="en-note">{tr('target.companyOnlyNote')}</p>}
            </div>
          </section>
        </div>

        <FactoryTargetModal
          isOpen={modal === 'target'}
          onClose={() => setModal(null)}
          onSave={async (targets) => { await updateFactoryTargets(targets); await load(true); }}
          factories={factories}
          canEdit={hasPermission('factory.update')}
          t={t}
        />
        <DailyReportModal isOpen={modal === 'daily'} onClose={() => setModal(null)} />
        <WeeklyReportModal isOpen={modal === 'weekly'} onClose={() => setModal(null)} />
        <MonthlyReportModal isOpen={modal === 'monthly'} onClose={() => setModal(null)} />
      </div>
    </MainLayout>
  );
}

export default Enterprise;
